import React, {useState} from 'react';
import {Box, Button, Grid, MobileStepper, Tooltip, Typography} from '@mui/material';
import {InfoOutlined, KeyboardArrowLeft, KeyboardArrowRight} from '@mui/icons-material';
import {Link} from 'react-router-dom';
import AppAccordion from '../../../../Component/Common/Components/Accordion/AppAccordion';
import {
    ImportProgressBarSummarize
} from '../../../../Component/Common/Components/ProgressBar/ImportProgressSummarize';
import {
    ImportManagement
} from '../../../../Component/Model/Dto/VerificationAgency/Client/ImportManagement/ImportManagement';
import colors from '../../../../assets/theme/base/colors';
import {ImportStep} from './ImportStep';

const maxTiles = 8;
const iconWidth = 45;
const iconHeight = 45;

interface IProps {
    importDetails: ImportManagement.ImportDetailDto[];
    progressData: { [scorecardId: string]: { [importTypeId: number]: ImportManagement.ImportDetailSheetDto } };
    useSignalR: boolean;
    isImportInProgressForOrganisation: boolean;
}

export const ResultsStep: React.FC<IProps> =
    ({
         importDetails,
         progressData,
         useSignalR,
         isImportInProgressForOrganisation
     }) => {

        const [activeStep, setActiveStep] = useState(0);

        const scorecardResults = Object.entries(progressData);
        const hasResults = scorecardResults.length > 0;

        const renderSheetsForStep = (scorecardId: string, stepIndex: number) => {
            const sheets = progressData[scorecardId];
            if (!sheets) return null;

            const importTypeIds = Object.keys(sheets);
            const startIndex = stepIndex * maxTiles;
            const endIndex = Math.min(startIndex + maxTiles, importTypeIds.length);

            return importTypeIds.slice(startIndex, endIndex).map(id => {
                const sheet = sheets[Number(id)];
                if (!sheet) return null;
                return (
                    <ImportProgressBarSummarize
                        value={sheet.progressPercentage ?? 0}
                        iconHeight={iconHeight}
                        iconWidth={iconWidth}
                        progressData={{
                            importTypeId: sheet.importTypeId,
                            importName: sheet.dataPageName,
                            numberOfRowsCorrect: sheet.numberOfRowsCorrect,
                            numberOfRowsFailed: sheet.numberOfRowsFailed,
                            isSuccess: sheet.importSuccessful,
                        }}
                    />
                );
            });
        };

        const describeResults = () => {
            if (!hasResults) {
                return 'Appears once an import starts.';
            }

            return (
                <>
                    What each scorecard received.
                    {isImportInProgressForOrganisation &&
                        <Tooltip title={<span>To view more information for imports in progress, click <Link
                            to={`payrollDetails/${useSignalR}`}>here</Link></span>}>
                            <InfoOutlined color="info" sx={{ml: 0.5, verticalAlign: 'middle'}}/>
                        </Tooltip>
                    }
                </>
            );
        };

        return (
            <ImportStep stepNumber={5} title="Results" description={describeResults()} pending={!hasResults}>
                {!hasResults &&
                    <Typography variant="caption" color="secondary">
                        Nothing has been imported for this organisation yet.
                    </Typography>
                }

                {scorecardResults.map(([scorecardId, sheets]) => {
                    const importDetail = importDetails.find(detail => detail.scorecardId === scorecardId);
                    const labelName = `${importDetail?.organisationName} | ${importDetail?.isEEScorecard ? 'EE' : 'BEE'} | ${importDetail?.scorecardName}`;
                    const importTypeIds = Object.keys(sheets);
                    const sheetMaxSteps = Math.ceil(importTypeIds.length / maxTiles);

                    return (
                        <AppAccordion
                            key={scorecardId}
                            expandOnDefault={true}
                            label={
                                <Box display="flex" alignItems="center">
                                    <Tooltip title={<span>To view more information and/or errors, click <Link
                                        to={`/organisation/payrollData/details/${importDetail?.scorecardId}/${importDetail?.id}/${useSignalR}`}>here</Link></span>}>
                                        <InfoOutlined sx={{mr: 1.5, color: colors.white.main}}/>
                                    </Tooltip>
                                    <span>{labelName}</span>
                                </Box>}
                        >
                            <Grid container item>
                                <Grid item xs={12} sx={{backgroundColor: colors.white.main}}>
                                    <Box sx={{height: "100%", width: "100%", pl: "15px", pt: "20px", pb: "20px"}}>
                                        <Grid container spacing={2}>
                                            {renderSheetsForStep(scorecardId, activeStep)}
                                        </Grid>
                                    </Box>
                                </Grid>

                                {sheetMaxSteps > 1 &&
                                    <Grid item xs={12}>
                                        <Box sx={{width: "100%"}}>
                                            <MobileStepper
                                                variant="dots"
                                                steps={sheetMaxSteps}
                                                position="static"
                                                activeStep={activeStep}
                                                sx={{background: colors.white.main}}
                                                nextButton={
                                                    <Button
                                                        size="small"
                                                        onClick={() =>
                                                            setActiveStep(prev => Math.min(prev + 1, sheetMaxSteps - 1))
                                                        }
                                                        disabled={activeStep === sheetMaxSteps - 1}
                                                    >
                                                        <KeyboardArrowRight/>
                                                    </Button>
                                                }
                                                backButton={
                                                    <Button
                                                        size="small"
                                                        onClick={() =>
                                                            setActiveStep(prev => Math.max(prev - 1, 0))
                                                        }
                                                        disabled={activeStep === 0}
                                                    >
                                                        <KeyboardArrowLeft/>
                                                    </Button>
                                                }
                                            />
                                        </Box>
                                    </Grid>
                                }
                            </Grid>
                        </AppAccordion>
                    );
                })}
            </ImportStep>
        );
    };
